import { useCallback } from "react";
import * as Immutable from "immutable";
import { useQuery, useQueries, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { create, windowScheduler, keyResolver } from "@yornaath/batshit";
import moment from "moment";
import { SecurityAppSecurityEvents } from "@graylog/enterprise-api";
import UserNotification from "util/UserNotification";
import { defaultOnError } from "util/conditional/onError";
import { RISK_SCORE_RANGE } from "security-app/components/SecurityEvents/Alerts/Constants";
import { Builder as FetchBuilder } from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import EnterpriseApiRoutes from "common/ApiRoutes";
import useOnRefresh from "components/common/PaginatedEntityTable/useOnRefresh";
import { slicesQueryKeyForColumn } from "components/common/PaginatedEntityTable/slicing/queryKeys";
import { investigationDetailsQueryKey, investigationEventsQueryKey } from "security-app/hooks/queryKeys";
import {
  fetchPaginatedSecurityEventEntities,
  newSecurityEvent,
  updateSecurityEvent,
  fetchUsersNTeams,
  triggerSecurityEventNotification,
  fetchEventDefinitions,
  fetchEntityTypes,
  toggleEventDefinitionStatus,
  copyEventDefinition,
  clearEventDefinitionNotificationsQueue,
  deleteEventDefinition,
  blkEnableDefinition,
  blkDisableDefinition,
  blkDeleteDefinition,
  fetchNotifications,
  fetchNotificationDetails,
  deleteNotification,
  testNotification,
  fetchEventDefinitionDetailsByIds,
  fetchSecurityEventByIds,
  triggerBulkSecurityEventNotification,
  securityEventEntitiesKeyFn,
  fetchSecurityEventsFilterOptions,
  fetchDetectionChainEventDefinitionSummary,
  fetchDetectionChainEvents
} from "./api/securityEventsAPI";
export const securityEventsKey = "get-security-events";
export const eventDefinitionsKey = "get-event-definitions";
const STATUS_SLICE_COLUMN = "status";
const OWNER_SLICE_COLUMN = "owner";
const invalidateSlicesForColumns = (queryClient, sliceColumns) => {
  sliceColumns.forEach(
    (sliceColumn) => queryClient.invalidateQueries({
      queryKey: slicesQueryKeyForColumn(sliceColumn)
    })
  );
};
const useInvalidateSecurityEvents = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (eventIds) => eventIds.forEach(
      (eventId) => queryClient.invalidateQueries({
        queryKey: [securityEventsKey, eventId]
      })
    ),
    [queryClient]
  );
};
const securityEventPipelines = create({
  fetcher: async (eventIds) => fetchSecurityEventByIds(eventIds),
  resolver: keyResolver("event_id"),
  scheduler: windowScheduler(10)
});
export const useGetSecurityEvent = (eventId) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [securityEventsKey, eventId],
    queryFn: () => securityEventPipelines.fetch(eventId),
    retry: 2,
    enabled: !!eventId
  });
  useOnRefresh(refetch);
  return {
    loadingSecurityEvents: isLoading,
    securityEvent: data
  };
};
const prepareFilters = (filters) => {
  let result = Immutable.OrderedMap();
  switch (filters.alerts) {
    case "only":
      result = result.set("alert", ["true"]);
      break;
    case "exclude":
      result = result.set("alert", ["false"]);
      break;
    default:
  }
  if (filters.timerange) {
    result = result.set("timestamp", [
      `${moment().subtract(Number(filters.timerange), "seconds").toISOString()}><${moment().toISOString()}`
    ]);
  }
  if (filters.associated_assets) {
    result = result.set("associated_assets", filters.associated_assets);
  }
  if (Array.isArray(filters.risk_score_range) && filters.risk_score_range.length > 0) {
    const [lowerBound = RISK_SCORE_RANGE.min, upperBound = RISK_SCORE_RANGE.max] = filters.risk_score_range;
    result = result.set("scores", [`${lowerBound}-${upperBound}`]);
  }
  return result;
};
export function useSearchSecurityEvents(userTimezone) {
  const searchSecurityEvents = async (searchParams) => fetchPaginatedSecurityEventEntities(searchParams, userTimezone);
  return { searchSecurityEvents, securityEventEntitiesKeyFn };
}
export function useGetSecurityEvents({ page, perPage, query = void 0, orderBy = void 0, direction = void 0, filters = void 0 }, userTimezone) {
  var _a, _b, _c, _d, _e;
  const { data, isLoading } = useQuery({
    queryKey: [securityEventsKey, page, perPage, query, orderBy, direction, filters, userTimezone],
    queryFn: () => {
      const payload = {
        page,
        pageSize: perPage,
        query,
        sort: { attributeId: orderBy, direction },
        filters: prepareFilters(filters)
      };
      return defaultOnError(
        fetchPaginatedSecurityEventEntities(payload, userTimezone),
        "Error fetching security events"
      );
    },
    retry: 2,
    placeholderData: keepPreviousData
  });
  return {
    loadingSecurityEvents: isLoading,
    securityEvents: isLoading ? [] : (data == null ? void 0 : data.list) || [],
    pagination: {
      page: ((_a = data == null ? void 0 : data.pagination) == null ? void 0 : _a.page) || page,
      perPage: ((_b = data == null ? void 0 : data.pagination) == null ? void 0 : _b.per_page) || perPage,
      total: ((_c = data == null ? void 0 : data.pagination) == null ? void 0 : _c.total) || 0,
      grandTotal: ((_d = data == null ? void 0 : data.pagination) == null ? void 0 : _d.total) || 0,
      count: ((_e = data == null ? void 0 : data.pagination) == null ? void 0 : _e.count) || 0
    }
  };
}
export function useFetchSecurityEventsFilterOptions() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: fetchSecurityEventsFilterOptions,
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    fetchSecurityEventsFilterOptions: mutateAsync,
    fetchingSecurityEventsFilterOptions: isLoading
  };
}
export function useCreateSecurityEvent() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: newSecurityEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [securityEventsKey] });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    createSecurityEvent: mutateAsync,
    creatingSecurityEvent: isLoading
  };
}
export function useUpdateSecurityEvent() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateSecurityEvent,
    onSuccess: () => {
      UserNotification.success("Event updated successfully");
      queryClient.invalidateQueries({ queryKey: [securityEventsKey] });
      queryClient.invalidateQueries({ queryKey: securityEventEntitiesKeyFn() });
      queryClient.invalidateQueries({ queryKey: investigationDetailsQueryKey() });
      queryClient.invalidateQueries({ queryKey: investigationEventsQueryKey() });
      invalidateSlicesForColumns(queryClient, [STATUS_SLICE_COLUMN, OWNER_SLICE_COLUMN]);
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    updateSecurityEvent: mutateAsync,
    updatingSecurityEvent: isLoading
  };
}
export function useGetUsersNTeams() {
  const { data, isLoading } = useQuery({
    queryKey: ["get-users-n-teams"],
    queryFn: () => defaultOnError(fetchUsersNTeams(), "Error fetching users & teams"),
    retry: 2
  });
  return {
    loadingUsersNTeams: isLoading,
    usersNTeams: data || []
  };
}
export function useTriggerSecurityEventNotification() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: triggerSecurityEventNotification,
    onSuccess: () => {
      UserNotification.success("Notification triggered successfully");
      queryClient.invalidateQueries({
        queryKey: ["trigger-security-event-notification"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    triggerSecurityEventNotification: mutateAsync,
    triggeringSecurityEventNotification: isLoading
  };
}
export function useBulkTriggerSecurityEventNotification() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: triggerBulkSecurityEventNotification,
    onSuccess: () => {
      UserNotification.success("Notifications triggered successfully");
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    triggerSecurityEventNotifications: mutateAsync,
    triggeringSecurityEventNotifications: isLoading
  };
}
export function useBulkSetOwnerForSecurityEvents() {
  const queryClient = useQueryClient();
  const invalidateSecurityEvents = useInvalidateSecurityEvents();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: ({ ids, ownerId }) => SecurityAppSecurityEvents.setOwnerForEvents({ event_ids: ids, owner_id: ownerId }),
    onSuccess: (_, { ids }) => {
      invalidateSecurityEvents(ids);
      invalidateSlicesForColumns(queryClient, [OWNER_SLICE_COLUMN]);
      UserNotification.success("Set owners successfully");
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    setOwner: mutateAsync,
    settingOwners: isLoading
  };
}
export function useBulkSetStatusForSecurityEvents() {
  const queryClient = useQueryClient();
  const invalidateSecurityEvents = useInvalidateSecurityEvents();
  const {
    mutateAsync,
    isPending: isLoading,
    isError,
    error
  } = useMutation({
    mutationFn: ({ ids, status }) => SecurityAppSecurityEvents.setStatusForEvents({ event_ids: ids, status }),
    onSuccess: (_, { ids }) => {
      invalidateSecurityEvents(ids);
      invalidateSlicesForColumns(queryClient, [STATUS_SLICE_COLUMN]);
      UserNotification.success("Set status successfully");
    }
  });
  return {
    setStatus: mutateAsync,
    settingStatus: isLoading,
    isError,
    error
  };
}
export function useBulkAddNotesForSecurityEvents() {
  const invalidateSecurityEvents = useInvalidateSecurityEvents();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: ({ ids, notes }) => SecurityAppSecurityEvents.addNotesForEvents({ event_ids: ids, notes }),
    onSuccess: (_, { ids }) => {
      invalidateSecurityEvents(ids);
      UserNotification.success("Added notes successfully");
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    addNotes: mutateAsync,
    addingNotes: isLoading
  };
}
export function useSetStatusForInvestigationEvents() {
  const queryClient = useQueryClient();
  const {
    mutateAsync,
    isPending: isLoading,
    isError,
    error
  } = useMutation({
    mutationFn: ({ investigationIds, status }) => new FetchBuilder("PUT", qualifyUrl(EnterpriseApiRoutes.SecurityEvents.investigation_status().url)).json({ investigation_ids: investigationIds, status }).ignoreUnauthorized().build(),
    onSuccess: (_, { investigationIds }) => {
      queryClient.invalidateQueries({ queryKey: [securityEventsKey] });
      investigationIds.forEach((id) => queryClient.invalidateQueries({ queryKey: ["investigation", id] }));
      invalidateSlicesForColumns(queryClient, [STATUS_SLICE_COLUMN]);
      UserNotification.success("Updated event statuses successfully");
    },
    onError: (apiError) => UserNotification.error(apiError.message)
  });
  return {
    setStatusForInvestigation: mutateAsync,
    settingStatus: isLoading,
    isError,
    error
  };
}
export function useGetEventDefinitions({
  page,
  perPage,
  query = void 0,
  orderBy = void 0,
  direction = void 0
}) {
  const { data, isLoading } = useQuery({
    queryKey: [eventDefinitionsKey, page, perPage, query, orderBy, direction],
    queryFn: () => defaultOnError(
      fetchEventDefinitions(page, perPage, query, orderBy, direction),
      "Error fetching event definitions"
    ),
    retry: 2,
    placeholderData: keepPreviousData
  });
  return {
    loadingEventDefinitions: isLoading,
    eventDefinitions: isLoading ? [] : (data == null ? void 0 : data.elements) || [],
    pagination: (data == null ? void 0 : data.pagination) || {
      total: 0,
      count: 0,
      page: 1,
      per_page: 10
    }
  };
}
const eventDefinitionDetailsKey = "get-event-definition-details";
export function useGetEventDefinitionDetails(eventDefinitionIds) {
  var _a;
  const queryClient = useQueryClient();
  const results = useQuery({
    queryKey: [eventDefinitionDetailsKey, eventDefinitionIds],
    queryFn: () => fetchEventDefinitionDetailsByIds(eventDefinitionIds).then((response) => {
      response.forEach(
        (eventDefinition) => queryClient.setQueryData([eventDefinitionDetailsKey, eventDefinition.id], eventDefinition)
      );
      return response;
    }),
    enabled: eventDefinitionIds.length > 0
  });
  return {
    loadingEventDefinition: results.isInitialLoading,
    eventDefinitions: (_a = results.data) != null ? _a : []
  };
}
const eventDefinitionPipelines = create({
  fetcher: async (eventDefinitionIds) => fetchEventDefinitionDetailsByIds(eventDefinitionIds),
  resolver: keyResolver("id"),
  scheduler: windowScheduler(10)
});
export const useGetEventDefinitionDetailsBatched = (eventDefinitionId) => {
  const { data, isLoading } = useQuery({
    queryKey: [eventDefinitionDetailsKey, eventDefinitionId],
    queryFn: () => defaultOnError(eventDefinitionPipelines.fetch(eventDefinitionId), "Fetching event definitions failed."),
    enabled: !!eventDefinitionId
  });
  return {
    loadingEventDefinition: isLoading,
    eventDefinition: data
  };
};
export function useGetEntityTypes() {
  const { data, isLoading } = useQuery({
    queryKey: ["get-entity-types"],
    queryFn: () => defaultOnError(fetchEntityTypes(), "Error fetching entity types"),
    retry: 2,
    placeholderData: keepPreviousData
  });
  return {
    loadingEntityTypes: isLoading,
    entityTypes: isLoading ? [] : data.processor_types.reduce((acc, procType) => {
      if (procType === "aggregation-v1") acc[procType] = "Filter & Aggregation";
      if (procType === "correlation-v1") acc[procType] = "Event Correlation";
      if (procType === "system-notifications-v1") acc[procType] = "System Notifications";
      if (procType === "anomaly-v1") acc[procType] = "Anomaly Detector";
      if (procType === "sigma-v1") acc[procType] = "Sigma Detector";
      return acc;
    }, {})
  };
}
export function useToggleEventDefinitionStatus() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: toggleEventDefinitionStatus,
    onSuccess: () => {
      UserNotification.success("Event definition status updated successfully");
      queryClient.invalidateQueries({
        queryKey: [eventDefinitionsKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    toggleEventDefinitionStatus: mutateAsync,
    togglingEventDefinitionStatus: isLoading
  };
}
export function useCopyEventDefinition() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: copyEventDefinition,
    onSuccess: () => {
      UserNotification.success("Event definition copied successfully");
      queryClient.invalidateQueries({
        queryKey: [eventDefinitionsKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    copyEventDefinition: mutateAsync,
    copyingEventDefinition: isLoading
  };
}
export function useClearEventDefinitionNotificationsQueue() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: clearEventDefinitionNotificationsQueue,
    onSuccess: () => {
      UserNotification.success("Event definition notifications queue cleared successfully");
      queryClient.invalidateQueries({
        queryKey: [eventDefinitionsKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    clearEventDefinitionNotificationsQueue: mutateAsync,
    clearingEventDefinitionNotificationsQueue: isLoading
  };
}
export function useDeleteEventDefinition() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteEventDefinition,
    onSuccess: () => {
      UserNotification.success("Event definition deleted successfully");
      queryClient.invalidateQueries({
        queryKey: [eventDefinitionsKey]
      });
      queryClient.invalidateQueries({
        queryKey: [securityEventsKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    deleteEventDefinition: mutateAsync,
    deletingEventDefinition: isLoading
  };
}
export function useBlkEnableDefinition() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: blkEnableDefinition,
    onSuccess: () => {
      UserNotification.success("Event definitions enabled successfully");
      queryClient.invalidateQueries({
        queryKey: [eventDefinitionsKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    blkEnableDefinition: mutateAsync,
    blkEnablingDefinition: isLoading
  };
}
export function useBlkDisableDefinition() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: blkDisableDefinition,
    onSuccess: () => {
      UserNotification.success("Event definitions disabled successfully");
      queryClient.invalidateQueries({
        queryKey: [eventDefinitionsKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    blkDisableDefinition: mutateAsync,
    blkDisablingDefinition: isLoading
  };
}
export function useBlkDeleteDefinition() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: blkDeleteDefinition,
    onSuccess: () => {
      UserNotification.success("Event definitions deleted successfully");
      queryClient.invalidateQueries({
        queryKey: [eventDefinitionsKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    blkDeleteDefinition: mutateAsync,
    blkDeletingDefinition: isLoading
  };
}
export function useGetNotifications({
  page,
  perPage,
  query = void 0,
  orderBy = void 0,
  direction = void 0
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-security-event-notifications", page, perPage, query, orderBy, direction],
    queryFn: () => defaultOnError(
      fetchNotifications(page, perPage, query, orderBy, direction),
      "Error fetching security event notifications"
    ),
    retry: 2,
    placeholderData: keepPreviousData
  });
  return {
    loadingNotifications: isLoading,
    notifications: isLoading ? [] : (data == null ? void 0 : data.elements) || [],
    pagination: {
      page: (data == null ? void 0 : data.pagination.page) || page,
      perPage: (data == null ? void 0 : data.pagination.per_page) || perPage,
      total: (data == null ? void 0 : data.pagination.total) || 0,
      grandTotal: (data == null ? void 0 : data.pagination.total) || 0,
      count: (data == null ? void 0 : data.pagination.count) || 0
    }
  };
}
export function useGetNotificationsDetails(notificationIds) {
  const results = useQueries({
    queries: notificationIds.map((notificationId) => ({
      queryKey: ["get-security-event-notification-details", notificationId],
      queryFn: () => defaultOnError(fetchNotificationDetails(notificationId), "Error fetching security event notification details"),
      enabled: notificationIds.length > 0
    }))
  });
  const { data, isLoading } = results.reduce(
    (acc, result) => {
      if (Array.isArray(acc.data)) acc.data.push(result.data);
      else acc.data = [result.data];
      acc.isLoading = results.some((res) => res.isLoading);
      return acc;
    },
    { data: [], isLoading: false }
  );
  return {
    loadingNotifications: isLoading,
    notifications: data
  };
}
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      UserNotification.success("Notification deleted successfully");
      queryClient.invalidateQueries({
        queryKey: ["get-security-event-notifications"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    deleteNotification: mutateAsync,
    deletingNotification: isLoading
  };
}
export function useTestNotification() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: testNotification,
    onSuccess: () => {
      UserNotification.success("Notification sent successfully");
      queryClient.invalidateQueries({
        queryKey: ["get-security-event-notifications"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    testNotification: mutateAsync,
    testingNotification: isLoading
  };
}
export function useDetectionChainEventDefinitionSummary(eventId) {
  const { data, isLoading } = useQuery({
    queryKey: ["detection-chain-event-definition-summary", eventId],
    queryFn: () => defaultOnError(
      fetchDetectionChainEventDefinitionSummary(eventId),
      "Error fetching Detection Chain details for the event definition"
    ),
    retry: 2,
    placeholderData: keepPreviousData,
    enabled: !!eventId
  });
  return {
    loadingDetectionChain: isLoading,
    detectionChain: data
  };
}
export function useDetectionChainEvents(detectionChainId) {
  const { data, isLoading } = useQuery({
    queryKey: ["detection-chain-events", detectionChainId],
    queryFn: () => defaultOnError(fetchDetectionChainEvents(detectionChainId), "Error fetching Detection Chain events"),
    retry: 2,
    placeholderData: keepPreviousData,
    enabled: !!detectionChainId
  });
  return {
    loadingDetectionChainEvents: isLoading,
    detectionChainEvents: data
  };
}
